
import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "./Button";
import { Link } from "react-router-dom";

type Slide = {
  id: number;
  image: string;
  title: string;
  description: string;
  cta: string;
  ctaLink: string;
};

const slides: Slide[] = [
  {
    id: 1,
    image: "/lovable-uploads/optimized/lawyers-outside-1200w.webp",
    title: "At This Stage, the Biggest Risk Isn't Using AI—It's Underestimating the Potential.",
    description: "Give your firm the Training and Education it needs to stop guessing and start mastering AI today.",
    cta: "Subscribe to our newsletter",
    ctaLink: "https://docketlabs.beehiiv.com/subscribe",
  },
  {
    id: 2,
    image: "/lovable-uploads/optimized/ai-insight-1200w.webp",
    title: "The Future of Your Firm Depends on What You Do with AI Right Now.",
    description: "Deploy tailored AI strategies, training, and solutions designed specifically for your legal practice.",
    cta: "View Our Services",
    ctaLink: "/services",
  },
  {
    id: 3,
    image: "/lovable-uploads/optimized/Winning-1200w.webp",
    title: "How Small Law Firms Are Using AI to Compete with Larger Practices",
    description: "Five case studies showing measurable AI results in legal practice",
    cta: "Download the Insights",
    ctaLink: "/insights/how-small-law-firms-are-using-ai",
  },
];

export const ImageCarouselHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 500);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);

    return () => clearInterval(timer);
  }, [currentSlide, isTransitioning]);

  return (
    <section className="w-full relative overflow-hidden bg-white min-h-screen">
      <div className="relative h-screen">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 w-full h-full transition-opacity duration-500 ease-in-out ${
              currentSlide === index ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <div className="absolute inset-0 overflow-hidden">
              {/* Replaced the purple gradient with a dark overlay for readability */}
              <div className="absolute inset-0 bg-black/40 z-20"></div>
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="absolute inset-0 z-30 flex items-center">
              <div className="container mx-auto px-6 flex justify-center">
                <div className="max-w-lg sm:max-w-xl md:max-w-2xl">
                  <div className="bg-black/50 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center">
                    <h2 className={`text-3xl md:text-4xl font-bold mb-4 text-white transition-all duration-700 ${
                      currentSlide === index ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    }`}>
                      {slide.title}
                    </h2>
                    <p className={`text-md md:text-lg text-white/90 mb-6 transition-all duration-700 delay-100 ${
                      currentSlide === index ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    }`}>
                      {slide.description}
                    </p>
                    <div className={`flex justify-center transition-all duration-700 delay-200 ${
                      currentSlide === index ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    }`}>
                      {slide.id === 1 ? (
                        <a href={slide.ctaLink} target="_blank" rel="noopener noreferrer">
                          <Button
                            variant="default"
                            size="lg"
                            className="bg-blue-600 hover:bg-blue-700 border border-blue-500/50 shadow-lg shadow-blue-500/20 text-white px-8"
                          >
                            {slide.cta}
                          </Button>
                        </a>
                      ) : (
                        <Link to={slide.ctaLink}>
                          <Button
                            variant="default"
                            size="lg"
                            className="bg-blue-600 hover:bg-blue-700 border border-blue-500/50 shadow-lg shadow-blue-500/20 text-white px-8"
                          >
                            {slide.cta}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-8 left-6 z-40 flex items-center space-x-8">
        <button
          onClick={prevSlide}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm text-white transition-colors"
          aria-label="Previous slide"
        >
          <ArrowLeft size={20} />
        </button>
        <button
          onClick={nextSlide}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm text-white transition-colors"
          aria-label="Next slide"
        >
          <ArrowRight size={20} />
        </button>
      </div>
    </section>
  );
};

export default ImageCarouselHero;
